import React, { useEffect, useState } from "react";
import { Badge } from "../components/index";
import { KNOWN_PROPOSAL_FEEDBACK_TAGS, type ProposalFeedbackTag } from "./chat/api";
import type {
  ProposalCardEdits,
  ProposalCardPayload,
  ProposalReminderChip,
} from "./proposalCardTypes";

// ---- Types ----

export type ApprovalType =
  | "payment"
  | "email_draft"
  | "email_send"
  | "tool_usage"
  | "pr_merge"
  | "code_change"
  | "proposal_card";

export interface InlineApprovalData {
  id: string;
  type: ApprovalType;
  status: "pending" | "approved" | "rejected" | "expired" | "auto_approved";
  priority: "low" | "normal" | "high" | "urgent";
  title: string;
  description: string;
  requestedBy: string;
  requestedAt: string;
  expiresAt?: string;
  payload: Record<string, unknown>;
}

interface InlineApprovalProps {
  approval: InlineApprovalData;
  /**
   * Approve callback. Receives the approval id, optional reviewer notes, and
   * optional structured edits (only emitted by the `proposal_card` renderer).
   */
  onApprove?: (id: string, notes?: string, edits?: ProposalCardEdits) => void;
  onReject?: (id: string, notes?: string) => void;
  /**
   * Undo callback. Visible on `proposal_card` approvals after Approve, while
   * `payload.executionState.undoUntil` has not yet passed.
   */
  onUndo?: (id: string) => void;
  /**
   * Retry callback. Visible on `proposal_card` approvals when execution
   * failed.
   */
  onRetry?: (id: string) => void;
  /**
   * Feedback callback. Visible on `proposal_card` approvals in terminal
   * phases (executed / failed). R5.4 (LA-028) — wires the thumbs-up/down
   * + 5 starter tag chips widget to the producer route at
   * `POST /api/proposals/:id/feedback`. The callback is invoked once per
   * rating click AND once per tag chip click (additive — no batching).
   */
  onFeedback?: (
    id: string,
    rating: "positive" | "negative",
    tags?: string[],
    comment?: string,
  ) => void;
}

// ---- Type display helpers ----

const TYPE_CONFIG: Record<string, { label: string; icon: string; color: string }> = {
  payment: { label: "Payment", icon: "$", color: "var(--c-success)" },
  email_draft: { label: "Email Draft", icon: "✉", color: "var(--c-info, var(--c-primary))" },
  email_send: { label: "Send Email", icon: "✉", color: "var(--c-info, var(--c-primary))" },
  // tool_usage is retained as the defensive SSE fallback at
  // hank/src/ui/workspace/chat/ChatSpine.tsx:124 — when an approval event
  // arrives without a `type` field, it gets stamped as "tool_usage".
  tool_usage: { label: "Tool Use", icon: "🔧", color: "var(--c-text-secondary)" },
  pr_merge: { label: "PR Merge", icon: "⎇", color: "var(--c-primary)" },
  code_change: { label: "Code Change", icon: "< >", color: "var(--c-primary)" },
  proposal_card: { label: "Proposal", icon: "✦", color: "var(--c-primary)" },
};

const PRIORITY_STYLES: Record<string, { variant: "default" | "amber" | "red"; label: string }> = {
  low: { variant: "default", label: "Low" },
  normal: { variant: "default", label: "Normal" },
  high: { variant: "amber", label: "High" },
  urgent: { variant: "red", label: "Urgent" },
};

// ---- Payload renderers ----

function PaymentPayload({ payload }: { payload: Record<string, unknown> }) {
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
      {payload.amount != null && (
        <>
          <span style={{ color: "var(--c-text-faint)" }}>Amount</span>
          <span className="font-medium" style={{ color: "var(--c-text)" }}>
            {payload.currency ?? "$"}
            {String(payload.amount)}
          </span>
        </>
      )}
      {payload.recipient && (
        <>
          <span style={{ color: "var(--c-text-faint)" }}>Recipient</span>
          <span style={{ color: "var(--c-text)" }}>{String(payload.recipient)}</span>
        </>
      )}
      {payload.method && (
        <>
          <span style={{ color: "var(--c-text-faint)" }}>Method</span>
          <span style={{ color: "var(--c-text)" }}>{String(payload.method)}</span>
        </>
      )}
    </div>
  );
}

function EmailPayload({
  payload,
  isPending,
  onBodyChange,
}: {
  payload: Record<string, unknown>;
  isPending?: boolean;
  /**
   * Phase 1 Slice 3: when supplied, the body field renders as a textarea
   * the operator can edit inline before approving. The handler captures the
   * edited body in onApproveWithEdits — see ProposalCardEdits.body. Style-
   * suggestion chips ("more formal", "shorter", "warmer") deferred to
   * Phase 1b/2.
   */
  onBodyChange?: (next: string) => void;
}) {
  const editable = Boolean(onBodyChange) && (isPending ?? true);
  return (
    <div className="space-y-1 text-xs">
      {payload.to && (
        <div className="flex gap-2">
          <span style={{ color: "var(--c-text-faint)" }}>To:</span>
          <span style={{ color: "var(--c-text)" }}>{String(payload.to)}</span>
        </div>
      )}
      {payload.subject && (
        <div className="flex gap-2">
          <span style={{ color: "var(--c-text-faint)" }}>Subject:</span>
          <span className="font-medium" style={{ color: "var(--c-text)" }}>
            {String(payload.subject)}
          </span>
        </div>
      )}
      {Boolean((payload.crmContext as { dealStage?: string } | undefined)?.dealStage) && (
        <div className="flex gap-2">
          <span style={{ color: "var(--c-text-faint)" }}>Deal:</span>
          <span style={{ color: "var(--c-text-secondary)" }}>
            {String(
              (payload.crmContext as { contactName?: string } | undefined)?.contactName ?? "",
            )}
            {(payload.crmContext as { dealStage?: string }).dealStage
              ? ` · ${(payload.crmContext as { dealStage: string }).dealStage}`
              : ""}
          </span>
        </div>
      )}
      {payload.body !== undefined &&
        (editable ? (
          <textarea
            aria-label="email-body-editor"
            value={String(payload.body ?? "")}
            onChange={(e) => onBodyChange!(e.target.value)}
            className="mt-1 w-full rounded p-2 text-[11px] whitespace-pre-wrap font-mono"
            style={{
              backgroundColor: "var(--c-input-bg, var(--c-bg))",
              border: "1px solid var(--c-border)",
              color: "var(--c-text)",
              minHeight: 100,
              resize: "vertical",
            }}
          />
        ) : (
          <div
            className="mt-1 p-2 rounded text-[11px] max-h-[160px] overflow-y-auto whitespace-pre-wrap"
            style={{
              backgroundColor: "var(--c-bg)",
              border: "1px solid var(--c-border)",
              color: "var(--c-text-secondary)",
            }}
          >
            {String(payload.body)}
          </div>
        ))}
    </div>
  );
}

function CodeChangePayload({ payload }: { payload: Record<string, unknown> }) {
  return (
    <div className="space-y-1 text-xs">
      {payload.repo && (
        <div className="flex gap-2">
          <span style={{ color: "var(--c-text-faint)" }}>Repo:</span>
          <span className="font-mono text-[11px]" style={{ color: "var(--c-text)" }}>
            {String(payload.repo)}
          </span>
        </div>
      )}
      {payload.files != null && (
        <div className="flex gap-2">
          <span style={{ color: "var(--c-text-faint)" }}>Files:</span>
          <span style={{ color: "var(--c-text)" }}>{String(payload.files)} changed</span>
        </div>
      )}
      {payload.branch && (
        <div className="flex gap-2">
          <span style={{ color: "var(--c-text-faint)" }}>Branch:</span>
          <span className="font-mono text-[11px]" style={{ color: "var(--c-text)" }}>
            {String(payload.branch)}
          </span>
        </div>
      )}
    </div>
  );
}

// ---- Proposal Card payload (Phase 1 birthday flow + future flows) ----

const DEFAULT_REMINDER_PRESETS: ProposalReminderChip[] = [
  { ruleKey: "14d_before", label: "14 days before", offsetDays: 14, timeOfDay: "09:00" },
  { ruleKey: "7d_before", label: "7 days before", offsetDays: 7, timeOfDay: "09:00" },
  { ruleKey: "1d_before", label: "1 day before", offsetDays: 1, timeOfDay: "09:00" },
  { ruleKey: "morning_of", label: "Morning of", offsetDays: 0, timeOfDay: "09:00" },
];

function isProposalCardPayload(payload: Record<string, unknown>): boolean {
  return (
    typeof payload === "object" &&
    payload !== null &&
    typeof (payload as { ledgerId?: unknown }).ledgerId === "string" &&
    typeof (payload as { templateId?: unknown }).templateId === "string"
  );
}

// ---- Feedback Widget (R5.4 — LA-028) ----

/**
 * 5 starter tag chips. Keys come from the single source of truth at
 * `chat/api.ts:KNOWN_PROPOSAL_FEEDBACK_TAGS` (the API contract layer);
 * display labels live here since they're a UI/presentation concern.
 * Keep `PROPOSAL_FEEDBACK_TAG_LABELS` in sync with the keys — TypeScript
 * `satisfies` enforces the key set at compile time, so adding a tag in
 * `chat/api.ts` without a matching label here is a build error rather
 * than silent "(?)" rendering.
 *
 * The canonical routing truth (which the consumer reads) lives at
 * `extensions/operator-model/src/learning/explicit-feedback-tag-map.ts`.
 */
const PROPOSAL_FEEDBACK_TAG_LABELS = {
  too_formal: "Too formal",
  too_casual: "Too casual",
  too_long: "Too long",
  too_short: "Too short",
  too_technical: "Too technical",
} as const satisfies Record<ProposalFeedbackTag, string>;

const PROPOSAL_FEEDBACK_TAGS: ReadonlyArray<{ key: ProposalFeedbackTag; label: string }> =
  KNOWN_PROPOSAL_FEEDBACK_TAGS.map((key) => ({
    key,
    label: PROPOSAL_FEEDBACK_TAG_LABELS[key],
  }));

function FeedbackWidget({
  approvalId,
  onFeedback,
}: {
  approvalId: string;
  onFeedback: (
    id: string,
    rating: "positive" | "negative",
    tags?: string[],
    comment?: string,
  ) => void;
}): React.ReactElement {
  const [rating, setRating] = useState<"positive" | "negative" | null>(null);
  const [submittedTags, setSubmittedTags] = useState<Set<string>>(new Set());
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState("");
  const [commentSubmitted, setCommentSubmitted] = useState(false);

  function handleRate(next: "positive" | "negative") {
    if (rating !== null) return; // ignore re-clicks once chosen
    setRating(next);
    onFeedback(approvalId, next);
  }

  function handleTag(tagKey: string) {
    if (rating === null) return;
    if (submittedTags.has(tagKey)) return;
    setSubmittedTags((prev) => {
      const next = new Set(prev);
      next.add(tagKey);
      return next;
    });
    onFeedback(approvalId, rating, [tagKey]);
  }

  function handleCommentSubmit() {
    if (rating === null) return;
    const trimmed = comment.trim();
    if (trimmed.length === 0) return;
    setCommentSubmitted(true);
    onFeedback(approvalId, rating, undefined, trimmed);
  }

  // Initial state: two small thumbs.
  if (rating === null) {
    return (
      <div
        aria-label="proposal-feedback-widget"
        className="flex items-center gap-2 mt-2 text-[11px]"
        style={{ color: "var(--c-text-faint)" }}
      >
        <span>Was this helpful?</span>
        <button
          type="button"
          onClick={() => handleRate("positive")}
          aria-label="proposal-feedback-positive"
          className="rounded px-1.5 py-0.5 transition-colors"
          style={{
            backgroundColor: "var(--c-surface)",
            border: "1px solid var(--c-border)",
            color: "var(--c-text-secondary)",
          }}
        >
          👍
        </button>
        <button
          type="button"
          onClick={() => handleRate("negative")}
          aria-label="proposal-feedback-negative"
          className="rounded px-1.5 py-0.5 transition-colors"
          style={{
            backgroundColor: "var(--c-surface)",
            border: "1px solid var(--c-border)",
            color: "var(--c-text-secondary)",
          }}
        >
          👎
        </button>
      </div>
    );
  }

  // Post-rating: chip row + optional comment toggle.
  return (
    <div aria-label="proposal-feedback-widget" className="mt-2 space-y-1.5">
      <div
        className="flex items-center gap-2 text-[11px]"
        style={{ color: "var(--c-text-secondary)" }}
      >
        <span aria-label="proposal-feedback-confirmation">
          Thanks for the feedback ✓ ({rating === "positive" ? "👍" : "👎"})
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5" aria-label="proposal-feedback-tags">
        {PROPOSAL_FEEDBACK_TAGS.map((tag) => {
          const done = submittedTags.has(tag.key);
          return (
            <button
              key={tag.key}
              type="button"
              onClick={() => handleTag(tag.key)}
              aria-label={`proposal-feedback-tag-${tag.key}`}
              disabled={done}
              className="rounded-full px-2 py-0.5 text-[11px] transition-colors"
              style={{
                backgroundColor: done ? "var(--c-surface-2)" : "var(--c-surface)",
                border: "1px solid var(--c-border)",
                color: done ? "var(--c-text-faint)" : "var(--c-text-secondary)",
                cursor: done ? "default" : "pointer",
              }}
            >
              {tag.label}
              {done && <span style={{ marginLeft: 4 }}>✓</span>}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setShowComment((v) => !v)}
          aria-label="proposal-feedback-comment-toggle"
          className="rounded-full px-2 py-0.5 text-[11px]"
          style={{
            backgroundColor: "var(--c-surface)",
            border: "1px dashed var(--c-border)",
            color: "var(--c-text-faint)",
          }}
        >
          {showComment ? "Hide note" : "Add note"}
        </button>
      </div>
      {showComment && !commentSubmitted && (
        <div className="flex items-start gap-2">
          <textarea
            aria-label="proposal-feedback-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Optional note..."
            className="flex-1 rounded p-1.5 text-[11px]"
            style={{
              backgroundColor: "var(--c-input-bg, var(--c-bg))",
              border: "1px solid var(--c-border)",
              color: "var(--c-text)",
              minHeight: 40,
              resize: "vertical",
            }}
          />
          <button
            type="button"
            onClick={handleCommentSubmit}
            aria-label="proposal-feedback-comment-submit"
            disabled={comment.trim().length === 0}
            className="text-[11px] px-2 py-1 rounded"
            style={{
              backgroundColor:
                comment.trim().length === 0 ? "var(--c-surface)" : "var(--c-primary)",
              color: comment.trim().length === 0 ? "var(--c-text-faint)" : "white",
              border: "1px solid var(--c-border)",
            }}
          >
            Done
          </button>
        </div>
      )}
      {commentSubmitted && (
        <div
          className="text-[10px]"
          style={{ color: "var(--c-text-faint)" }}
          aria-label="proposal-feedback-comment-confirmation"
        >
          Note saved ✓
        </div>
      )}
    </div>
  );
}

function ProposalCardPayloadRenderer({
  approvalId,
  payload,
  isPending,
  onApproveWithEdits,
  onReject,
  onUndo,
  onRetry,
  onFeedback,
}: {
  approvalId: string;
  payload: ProposalCardPayload;
  isPending: boolean;
  onApproveWithEdits: (edits?: ProposalCardEdits) => void;
  onReject: () => void;
  onUndo?: () => void;
  onRetry?: () => void;
  onFeedback?: (
    id: string,
    rating: "positive" | "negative",
    tags?: string[],
    comment?: string,
  ) => void;
}): React.ReactElement {
  const initialReminders = payload.reminders ?? DEFAULT_REMINDER_PRESETS;
  const [date, setDate] = useState<string>(payload.date ?? "");
  const [recurringPattern, setRecurringPattern] = useState(payload.recurringPattern ?? "yearly");
  const [reminders, setReminders] = useState<ProposalReminderChip[]>(initialReminders);
  const [contactLinkOn, setContactLinkOn] = useState<boolean>(
    payload.contactLink?.suggested ?? false,
  );
  const [editingDetails, setEditingDetails] = useState(false);
  // Live ticker for undo affordance — re-render every second while undoUntil is in the future.
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!payload.executionState?.undoUntil) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [payload.executionState?.undoUntil]);

  const phase = payload.executionState?.phase ?? "draft";
  const undoUntilMs = payload.executionState?.undoUntil
    ? Date.parse(payload.executionState.undoUntil)
    : null;
  const undoVisible = undoUntilMs !== null && undoUntilMs > now;
  const undoSecondsRemaining = undoVisible
    ? Math.max(0, Math.ceil((undoUntilMs! - now) / 1000))
    : 0;

  function buildEdits(): ProposalCardEdits | undefined {
    const edits: ProposalCardEdits = {};
    if (date && date !== payload.date) edits.date = date;
    if (recurringPattern !== payload.recurringPattern) edits.recurringPattern = recurringPattern;
    if (!sameReminders(reminders, payload.reminders ?? DEFAULT_REMINDER_PRESETS)) {
      edits.reminders = reminders;
    }
    if (contactLinkOn !== (payload.contactLink?.suggested ?? false)) {
      edits.contactLink = {
        suggested: contactLinkOn,
        contactId: payload.contactLink?.contactId,
      };
    }
    return Object.keys(edits).length === 0 ? undefined : edits;
  }

  function removeChip(ruleKey: string): void {
    setReminders((prev) => prev.filter((r) => r.ruleKey !== ruleKey));
  }

  function addChip(): void {
    const nextRule = DEFAULT_REMINDER_PRESETS.find(
      (preset) => !reminders.some((r) => r.ruleKey === preset.ruleKey),
    );
    if (nextRule) setReminders((prev) => [...prev, nextRule]);
  }

  return (
    <div className="space-y-3 text-xs">
      {/* Defaults summary */}
      {payload.defaultsApplied && payload.defaultsApplied.length > 0 && (
        <div
          aria-label="defaults-applied"
          className="rounded p-2 text-[11px]"
          style={{ backgroundColor: "var(--c-bg)", color: "var(--c-text-secondary)" }}
        >
          <div className="font-medium mb-1" style={{ color: "var(--c-text)" }}>
            Defaults applied
          </div>
          <ul className="list-disc ml-4 space-y-0.5">
            {payload.defaultsApplied.map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Person + date row */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        {payload.personName && (
          <>
            <span style={{ color: "var(--c-text-faint)" }}>Person</span>
            <span style={{ color: "var(--c-text)" }}>{payload.personName}</span>
          </>
        )}
        {payload.eventType && (
          <>
            <span style={{ color: "var(--c-text-faint)" }}>Type</span>
            <span style={{ color: "var(--c-text)" }}>{payload.eventType}</span>
          </>
        )}
        <span style={{ color: "var(--c-text-faint)" }}>Date</span>
        <input
          aria-label="proposal-date"
          type="text"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          disabled={!isPending}
          className="rounded px-1.5 py-0.5 text-xs"
          style={{
            backgroundColor: "var(--c-input-bg)",
            border: "1px solid var(--c-border)",
            color: "var(--c-text)",
          }}
        />
        <span style={{ color: "var(--c-text-faint)" }}>Recurrence</span>
        <select
          aria-label="proposal-recurrence"
          value={recurringPattern}
          onChange={(e) => setRecurringPattern(e.target.value as typeof recurringPattern)}
          disabled={!isPending}
          className="rounded px-1.5 py-0.5 text-xs"
          style={{
            backgroundColor: "var(--c-input-bg)",
            border: "1px solid var(--c-border)",
            color: "var(--c-text)",
          }}
        >
          <option value="yearly">yearly</option>
          <option value="monthly">monthly</option>
          <option value="weekly">weekly</option>
          <option value="daily">daily</option>
          <option value="none">none</option>
        </select>
      </div>

      {/* Reminder chips */}
      <div>
        <div className="mb-1" style={{ color: "var(--c-text-faint)" }}>
          Reminders
        </div>
        <div className="flex flex-wrap gap-1.5" aria-label="proposal-reminders">
          {reminders.map((chip) => (
            <button
              key={chip.ruleKey}
              type="button"
              aria-label={`reminder-chip-${chip.ruleKey}`}
              disabled={!isPending}
              onClick={() => removeChip(chip.ruleKey)}
              className="rounded-full px-2 py-0.5 text-[11px] transition-colors"
              style={{
                backgroundColor: "var(--c-surface-2)",
                border: "1px solid var(--c-border)",
                color: "var(--c-text)",
                cursor: isPending ? "pointer" : "default",
              }}
            >
              {chip.label}
              {isPending && <span style={{ color: "var(--c-text-faint)", marginLeft: 4 }}>×</span>}
            </button>
          ))}
          {isPending && reminders.length < DEFAULT_REMINDER_PRESETS.length && (
            <button
              type="button"
              aria-label="reminder-chip-add"
              onClick={addChip}
              className="rounded-full px-2 py-0.5 text-[11px]"
              style={{
                backgroundColor: "var(--c-surface)",
                border: "1px dashed var(--c-border)",
                color: "var(--c-text-secondary)",
              }}
            >
              + add
            </button>
          )}
        </div>
      </div>

      {/* Contact link */}
      {payload.contactLink && (
        <div className="flex items-center gap-2">
          <span style={{ color: "var(--c-text-faint)" }}>Link contact</span>
          <button
            type="button"
            role="switch"
            aria-checked={contactLinkOn}
            aria-label="contact-link-toggle"
            disabled={!isPending}
            onClick={() => setContactLinkOn((v) => !v)}
            className="rounded-full px-2 py-0.5 text-[11px]"
            style={{
              backgroundColor: contactLinkOn ? "var(--c-success)" : "var(--c-surface)",
              border: "1px solid var(--c-border)",
              color: contactLinkOn ? "white" : "var(--c-text-secondary)",
            }}
          >
            {contactLinkOn ? "✓ Linked" : "Suggest"}
          </button>
          {payload.contactLink.contactName && (
            <span style={{ color: "var(--c-text-secondary)" }}>
              {payload.contactLink.contactName}
            </span>
          )}
        </div>
      )}

      {/* Execution phase visualization */}
      {phase === "running" && (
        <div
          aria-label="proposal-running"
          className="flex items-center gap-2 text-[11px]"
          style={{ color: "var(--c-text-secondary)" }}
        >
          <span>Executing…</span>
        </div>
      )}
      {phase === "executed" && (
        <>
          <div
            aria-label="proposal-executed"
            className="flex items-center gap-2 text-[11px]"
            style={{ color: "var(--c-success)" }}
          >
            <span>✓ Done</span>
            {undoVisible && (
              <button
                type="button"
                onClick={onUndo}
                aria-label="proposal-undo"
                className="ml-2 underline"
                style={{ color: "var(--c-text-secondary)" }}
              >
                Undo ({undoSecondsRemaining}s)
              </button>
            )}
          </div>
          {onFeedback && <FeedbackWidget approvalId={approvalId} onFeedback={onFeedback} />}
        </>
      )}
      {phase === "failed" && payload.executionState?.error && (
        <>
          <div
            aria-label="proposal-failed"
            className="rounded p-2 text-[11px]"
            style={{
              backgroundColor: "rgba(239, 68, 68, 0.06)",
              border: "1px solid var(--c-danger)",
              color: "var(--c-text)",
            }}
          >
            <div style={{ color: "var(--c-danger)", fontWeight: 500 }}>Execution failed</div>
            <div style={{ color: "var(--c-text-secondary)" }}>
              {payload.executionState.error.code}: {payload.executionState.error.message}
            </div>
            {payload.executionState.error.suggestedRecovery && (
              <div className="mt-1" style={{ color: "var(--c-text-secondary)" }}>
                {payload.executionState.error.suggestedRecovery}
              </div>
            )}
            <button
              type="button"
              onClick={onRetry}
              aria-label="proposal-retry"
              className="mt-1 underline"
              style={{ color: "var(--c-primary)" }}
            >
              Retry
            </button>
          </div>
          {onFeedback && <FeedbackWidget approvalId={approvalId} onFeedback={onFeedback} />}
        </>
      )}

      {/* Approve / Reject — pending only */}
      {isPending && (
        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            onClick={() => onApproveWithEdits(buildEdits())}
            aria-label="proposal-approve"
            className="text-xs px-3 py-1.5 rounded-lg font-medium transition-colors"
            style={{ backgroundColor: "var(--c-primary)", color: "white" }}
          >
            Approve
          </button>
          <button
            type="button"
            onClick={onReject}
            aria-label="proposal-reject"
            className="text-xs px-3 py-1.5 rounded-lg font-medium transition-colors"
            style={{
              backgroundColor: "var(--c-surface)",
              color: "var(--c-text-secondary)",
              border: "1px solid var(--c-border)",
            }}
          >
            Reject
          </button>
          <button
            type="button"
            onClick={() => setEditingDetails((v) => !v)}
            aria-label="proposal-edit-toggle"
            className="text-[10px] ml-auto"
            style={{ color: "var(--c-text-faint)" }}
          >
            {editingDetails ? "Hide details" : "Edit details"}
          </button>
        </div>
      )}
    </div>
  );

  function sameReminders(a: ProposalReminderChip[], b: ProposalReminderChip[]): boolean {
    if (a.length !== b.length) return false;
    const aKeys = a.map((r) => r.ruleKey).sort();
    const bKeys = b.map((r) => r.ruleKey).sort();
    return aKeys.every((k, i) => k === bKeys[i]);
  }
}

function GenericPayload({ payload }: { payload: Record<string, unknown> }) {
  const entries = Object.entries(payload).slice(0, 5);
  if (entries.length === 0) return null;
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
      {entries.map(([key, val]) => (
        <React.Fragment key={key}>
          <span style={{ color: "var(--c-text-faint)" }}>{key}</span>
          <span style={{ color: "var(--c-text)" }}>{String(val)}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

function renderPayload(
  type: string,
  payload: Record<string, unknown>,
  ctx?: {
    approvalId: string;
    isPending: boolean;
    onApproveWithEdits: (edits?: ProposalCardEdits) => void;
    onReject: () => void;
    onUndo?: () => void;
    onRetry?: () => void;
    onFeedback?: (
      id: string,
      rating: "positive" | "negative",
      tags?: string[],
      comment?: string,
    ) => void;
  },
) {
  switch (type) {
    case "payment":
      return <PaymentPayload payload={payload} />;
    case "email_draft":
    case "email_send":
      return <EmailPayload payload={payload} />;
    case "code_change":
    case "pr_merge":
      return <CodeChangePayload payload={payload} />;
    case "proposal_card":
      if (ctx && isProposalCardPayload(payload)) {
        return (
          <ProposalCardPayloadRenderer
            approvalId={ctx.approvalId}
            payload={payload as unknown as ProposalCardPayload}
            isPending={ctx.isPending}
            onApproveWithEdits={ctx.onApproveWithEdits}
            onReject={ctx.onReject}
            onUndo={ctx.onUndo}
            onRetry={ctx.onRetry}
            onFeedback={ctx.onFeedback}
          />
        );
      }
      return <GenericPayload payload={payload} />;
    default:
      return <GenericPayload payload={payload} />;
  }
}

// ---- Component ----

export default function InlineApproval({
  approval,
  onApprove,
  onReject,
  onUndo,
  onRetry,
  onFeedback,
}: InlineApprovalProps): React.ReactElement {
  const [reviewNotes, setReviewNotes] = useState("");
  const [showNotes, setShowNotes] = useState(false);
  const [resolved, setResolved] = useState<"approved" | "rejected" | null>(
    approval.status === "approved"
      ? "approved"
      : approval.status === "rejected"
        ? "rejected"
        : null,
  );

  const typeConfig = TYPE_CONFIG[approval.type] ?? {
    label: approval.type,
    icon: "?",
    color: "var(--c-text-secondary)",
  };
  const priorityInfo = PRIORITY_STYLES[approval.priority] ?? PRIORITY_STYLES.normal;
  const isPending = approval.status === "pending" && resolved === null;
  const isProposalCard = approval.type === "proposal_card";

  const handleApprove = () => {
    onApprove?.(approval.id, reviewNotes || undefined);
    setResolved("approved");
    setShowNotes(false);
    setReviewNotes("");
  };

  const handleReject = () => {
    onReject?.(approval.id, reviewNotes || undefined);
    setResolved("rejected");
    setShowNotes(false);
    setReviewNotes("");
  };

  const handleProposalApproveWithEdits = (edits?: ProposalCardEdits) => {
    onApprove?.(approval.id, undefined, edits);
    setResolved("approved");
  };

  const handleProposalReject = () => {
    onReject?.(approval.id, undefined);
    setResolved("rejected");
  };

  return (
    <div
      className="rounded-xl overflow-hidden my-2"
      style={{
        border: `1px solid ${resolved === "approved" ? "var(--c-success)" : resolved === "rejected" ? "var(--c-danger)" : "var(--c-warning)"}`,
        backgroundColor: "var(--c-surface)",
        opacity: resolved ? 0.85 : 1,
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-2.5"
        style={{
          backgroundColor:
            resolved === "approved"
              ? "rgba(34, 197, 94, 0.06)"
              : resolved === "rejected"
                ? "rgba(239, 68, 68, 0.06)"
                : "rgba(234, 179, 8, 0.06)",
          borderBottom: "1px solid var(--c-border)",
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          {/* Type icon */}
          <span
            className="shrink-0 w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold"
            style={{
              backgroundColor: "var(--c-surface)",
              color: typeConfig.color,
              border: "1px solid var(--c-border)",
            }}
          >
            {typeConfig.icon}
          </span>
          <span className="text-sm font-medium truncate" style={{ color: "var(--c-text)" }}>
            {approval.title}
          </span>
          <Badge variant={priorityInfo.variant} size="sm">
            {priorityInfo.label}
          </Badge>
          {resolved && (
            <Badge variant={resolved === "approved" ? "green" : "red"} size="sm">
              {resolved === "approved" ? "Approved" : "Rejected"}
            </Badge>
          )}
        </div>
        <span className="text-[10px] shrink-0 ml-2" style={{ color: "var(--c-text-faint)" }}>
          {typeConfig.label}
        </span>
      </div>

      {/* Body: description + payload */}
      <div className="px-4 py-3 space-y-2">
        <p className="text-xs" style={{ color: "var(--c-text-secondary)" }}>
          {approval.description}
        </p>
        {renderPayload(approval.type, approval.payload, {
          approvalId: approval.id,
          isPending,
          onApproveWithEdits: handleProposalApproveWithEdits,
          onReject: handleProposalReject,
          onUndo: onUndo ? () => onUndo(approval.id) : undefined,
          onRetry: onRetry ? () => onRetry(approval.id) : undefined,
          onFeedback,
        })}
      </div>

      {/* Actions (only for pending non-proposal-card approvals — proposal_card
          renders its own approve/reject inline). */}
      {isPending && !isProposalCard && (
        <div
          className="px-4 py-2.5 space-y-2"
          style={{ borderTop: "1px solid var(--c-border)", backgroundColor: "var(--c-surface-2)" }}
        >
          {/* Notes input (toggleable) */}
          {showNotes && (
            <textarea
              placeholder="Add review notes (optional)..."
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              className="w-full rounded-lg px-3 py-2 text-xs"
              style={{
                backgroundColor: "var(--c-input-bg)",
                border: "1px solid var(--c-border)",
                color: "var(--c-text)",
                minHeight: 40,
                resize: "vertical",
              }}
            />
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={handleApprove}
              className="text-xs px-3 py-1.5 rounded-lg font-medium transition-colors"
              style={{
                backgroundColor: "var(--c-success)",
                color: "white",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.85")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
            >
              Approve
            </button>
            <button
              onClick={handleReject}
              className="text-xs px-3 py-1.5 rounded-lg font-medium transition-colors"
              style={{
                backgroundColor: "var(--c-surface)",
                color: "var(--c-danger)",
                border: "1px solid var(--c-danger)",
              }}
            >
              Reject
            </button>
            <button
              onClick={() => setShowNotes(!showNotes)}
              className="text-[10px] px-2 py-1 rounded transition-colors ml-auto"
              style={{ color: "var(--c-text-faint)" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--c-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
            >
              {showNotes ? "Hide notes" : "Add notes"}
            </button>
          </div>
        </div>
      )}

      {/* Timestamp footer */}
      <div
        className="flex items-center justify-between px-4 py-1.5"
        style={{ borderTop: "1px solid var(--c-border)" }}
      >
        <span className="text-[10px]" style={{ color: "var(--c-text-faint)" }}>
          Requested by {approval.requestedBy}
        </span>
        <span className="text-[10px]" style={{ color: "var(--c-text-faint)" }}>
          {new Date(approval.requestedAt).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      </div>
    </div>
  );
}
